import { useState } from "react";
import { X, Loader2, Check, ChevronDown, ArrowRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const webServicesOptions = [
  "Website Design", "Website Redesign", "Landing Pages", "Managed Hosting",
  "Website Care Plans", "Website Security", "Website Optimisation",
  "One-Off Website Support", "Website Transfers", "Other", "Not Sure",
];

const businessServicesOptions = [
  "Business Email / Microsoft 365", "Google Business Profile",
  "Website Reporting", "Content & Blog Writing", "Website Assistants",
  "Other", "Not Sure",
];

export default function EnquiryModal({ isOpen, onClose }) {
  const [form, setForm] = useState({ name: "", businessName: "", email: "", phone: "", serviceCategory: "", helpWith: "", otherDetails: "", briefDescription: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "serviceCategory") {
      setForm((p) => ({ ...p, serviceCategory: value, helpWith: "", otherDetails: "" }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleClose = () => {
    onClose();
    setStatus(null);
    setForm({ name: "", businessName: "", email: "", phone: "", serviceCategory: "", helpWith: "", otherDetails: "", briefDescription: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await axios.post(`${API}/homepage-enquiry`, form);
      if (res.data.success) setStatus({ type: "success", message: res.data.message });
    } catch {
      setStatus({ type: "error", message: "Something went wrong. Please try again or call us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  const helpOptions = form.serviceCategory === "Web Services" ? webServicesOptions : form.serviceCategory === "Business Services" ? businessServicesOptions : [];
  const inputClass = "w-full px-4 py-3 border border-zinc-200 bg-white text-zinc-900 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-sm";
  const labelClass = "block text-sm font-semibold text-zinc-900 mb-2";
  const selectClass = "w-full px-4 py-3 border border-zinc-200 bg-white text-zinc-900 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-sm appearance-none";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }} data-testid="enquiry-overlay">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-0 shadow-2xl" data-testid="enquiry-modal">
        <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 pt-6 pb-4 flex items-start justify-between rounded-t-2xl z-10">
          <div>
            <h3 className="text-xl font-extrabold text-zinc-900">Send an Enquiry</h3>
            <p className="text-sm text-zinc-500 mt-1">Tell us what you need and we'll get back to you.</p>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="enquiry-modal-close"><X className="w-5 h-5 text-zinc-400" /></button>
        </div>

        {status?.type === "success" ? (
          <div className="p-6 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-green-600" /></div>
            <h4 className="text-lg font-bold text-zinc-900 mb-2">Enquiry Submitted</h4>
            <p className="text-sm text-zinc-600">{status.message}</p>
            <button onClick={handleClose} className="btn-primary px-6 py-2.5 mt-6 font-bold rounded-[10px]" data-testid="enquiry-success-close">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="enquiry-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Your Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required className={inputClass} data-testid="enquiry-name" />
              </div>
              <div>
                <label className={labelClass}>Business Name *</label>
                <input name="businessName" value={form.businessName} onChange={handleChange} required className={inputClass} data-testid="enquiry-business" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Email Address *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required className={inputClass} data-testid="enquiry-email" />
            </div>
            <div>
              <label className={labelClass}>Phone Number <span className="font-normal text-zinc-400">(optional)</span></label>
              <input name="phone" value={form.phone} onChange={handleChange} className={inputClass} data-testid="enquiry-phone" />
            </div>

            <div>
              <label className={labelClass}>What kind of service are you interested in? *</label>
              <div className="relative">
                <select name="serviceCategory" value={form.serviceCategory} onChange={handleChange} required className={selectClass} data-testid="enquiry-service-category">
                  <option value="">Select a category</option>
                  <option value="Web Services">Web Services</option>
                  <option value="Business Services">Business Services</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
              </div>
            </div>

            {form.serviceCategory && (
              <div className="animate-fade-in">
                <label className={labelClass}>What do you need help with? *</label>
                <div className="relative">
                  <select name="helpWith" value={form.helpWith} onChange={handleChange} required className={selectClass} data-testid="enquiry-help-select">
                    <option value="">Select a service</option>
                    {helpOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                </div>
              </div>
            )}

            {form.helpWith === "Other" && (
              <div className="animate-fade-in">
                <label className={labelClass}>What do you need help with? *</label>
                <input name="otherDetails" value={form.otherDetails} onChange={handleChange} required className={inputClass} data-testid="enquiry-other" />
              </div>
            )}

            <div>
              <label className={labelClass}>Briefly tell us what you need *</label>
              <textarea name="briefDescription" value={form.briefDescription} onChange={handleChange} required rows={3} className={inputClass} data-testid="enquiry-description" />
            </div>

            {status?.type === "error" && <p className="text-sm text-red-600">{status.message}</p>}

            <button type="submit" disabled={submitting} className="btn-primary w-full py-3.5 font-bold rounded-[10px] inline-flex items-center justify-center gap-2" data-testid="enquiry-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send My Enquiry <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
